import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import api, { formatApiErrorDetail, getPublicUploadsOrigin } from '../services/api';

const OrdenesNotificadasAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [items, setItems] = useState([]);
  const [convocatorias, setConvocatorias] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [searchConvocatoria, setSearchConvocatoria] = useState('');
  const [showConvocatoriaDropdown, setShowConvocatoriaDropdown] = useState(false);
  const [form, setForm] = useState({
    id_ganador: '',
    id_proveedor: '',
    id_convocatoria: '',
    observaciones: '',
    orden_pdf: null,
  });

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [itemsRes, convRes] = await Promise.all([
        api.get('/api/v1/ordenes-notificadas/'),
        api.get('/api/v1/convocatorias/'),
      ]);
      setItems(itemsRes.data || []);
      setConvocatorias(convRes.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e, 'Error al cargar órdenes notificadas'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const onChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const convocatoriaDisplayLabel = (c) =>
    `${c.COD_CONVOCATORIA || '—'} — ${c.concepto || 'Sin concepto'} (Nº ${c.nro_convocatoria})`;

  const getEstadoBadgeVariant = (estado) => {
    switch (estado) {
      case 'ABIERTA':
        return 'success';
      case 'CERRADA':
        return 'secondary';
      case 'EVALUACION':
        return 'warning';
      case 'FINALIZADA':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getFilteredConvocatorias = () => {
    const q = searchConvocatoria.trim().toLowerCase();
    if (!q) return convocatorias.slice(0, 15);
    return convocatorias
      .filter(
        (c) =>
          (c.COD_CONVOCATORIA && String(c.COD_CONVOCATORIA).toLowerCase().includes(q)) ||
          (c.concepto && c.concepto.toLowerCase().includes(q)) ||
          (c.rubro && c.rubro.toLowerCase().includes(q)) ||
          String(c.nro_convocatoria).includes(q)
      )
      .slice(0, 20);
  };

  const handleSearchConvocatoriaChange = (e) => {
    const value = e.target.value;
    setSearchConvocatoria(value);
    setShowConvocatoriaDropdown(true);
    if (form.id_convocatoria) {
      const c = convocatorias.find((x) => String(x.nro_convocatoria) === String(form.id_convocatoria));
      const expected = c ? convocatoriaDisplayLabel(c) : '';
      if (value !== expected) {
        onConvocatoriaChange('');
      }
    }
  };

  const handlePickConvocatoria = (conv) => {
    setShowConvocatoriaDropdown(false);
    setSearchConvocatoria(convocatoriaDisplayLabel(conv));
    onConvocatoriaChange(String(conv.nro_convocatoria));
  };

  const onConvocatoriaChange = async (nroStr) => {
    onChange('id_convocatoria', nroStr);
    setPreview(null);
    setForm((prev) => ({
      ...prev,
      id_convocatoria: nroStr,
      id_ganador: '',
      id_proveedor: '',
    }));

    if (!nroStr) {
      return;
    }

    const nro = parseInt(nroStr, 10);
    if (Number.isNaN(nro)) return;

    setLoadingPreview(true);
    setError('');
    try {
      const { data } = await api.get(`/api/v1/ordenes-notificadas/preview/${nro}`);
      setPreview(data);
      if (data.tiene_ganador && data.id_proveedor != null) {
        setForm((prev) => ({
          ...prev,
          id_convocatoria: nroStr,
          id_proveedor: String(data.id_proveedor),
          id_ganador: data.id_ganador != null ? String(data.id_ganador) : '',
        }));
      }
    } catch (e) {
      setPreview(null);
      setError(formatApiErrorDetail(e, 'No se pudieron cargar los datos del ganador'));
    } finally {
      setLoadingPreview(false);
    }
  };

  const puedeEnviar =
    form.id_convocatoria &&
    form.id_proveedor &&
    form.id_ganador &&
    form.orden_pdf &&
    preview?.tiene_ganador &&
    !preview?.orden_ya_notificada;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!puedeEnviar) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      fd.append('id_ganador', String(form.id_ganador));
      fd.append('id_proveedor', String(form.id_proveedor));
      fd.append('id_convocatoria', String(form.id_convocatoria));
      if (form.observaciones) fd.append('observaciones', form.observaciones);
      if (form.orden_pdf) fd.append('orden_pdf', form.orden_pdf);

      await api.post('/api/v1/ordenes-notificadas/', fd);
      setSuccess('Orden notificada correctamente. Se envió correo y notificación interna.');
      setForm({
        id_ganador: '',
        id_proveedor: '',
        id_convocatoria: '',
        observaciones: '',
        orden_pdf: null,
      });
      setSearchConvocatoria('');
      setPreview(null);
      await loadData();
    } catch (e2) {
      setError(formatApiErrorDetail(e2, 'No se pudo notificar la orden'));
    } finally {
      setSaving(false);
    }
  };

  const fileUrl = (item) => {
    const cod = (item.cod_convocatoria || item.id_convocatoria || '').toString().trim().replace(/\s+/g, '_');
    return `${getPublicUploadsOrigin()}/uploads/ordenes_notificadas/${cod}/${item.orden_pdf}`;
  };

  const convLabel = (nro) => {
    const c = convocatorias.find((x) => String(x.nro_convocatoria) === String(nro));
    if (!c) return nro;
    return `${c.COD_CONVOCATORIA || nro}${c.concepto ? ` — ${c.concepto}` : ''}`;
  };

  const filteredConvocatorias = getFilteredConvocatorias();

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1 fw-bold">Órdenes notificadas</h1>
          <p className="text-muted mb-0">
            Elija la convocatoria: el sistema completa al proveedor ganador según la evaluación. Solo adjunte el PDF
            de la orden y envíe.
          </p>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-3">Nueva notificación de orden</h5>
          <Form onSubmit={onSubmit}>
            <Row className="g-3">
              <Col md={12}>
                <Form.Label className="fw-semibold">1. Convocatoria</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type="text"
                    autoComplete="off"
                    placeholder="Buscar por código, concepto, rubro o Nº de convocatoria…"
                    value={searchConvocatoria}
                    onChange={handleSearchConvocatoriaChange}
                    onFocus={() => {
                      setShowConvocatoriaDropdown(true);
                      if (form.id_convocatoria && !searchConvocatoria) {
                        const c = convocatorias.find(
                          (x) => String(x.nro_convocatoria) === String(form.id_convocatoria)
                        );
                        if (c) setSearchConvocatoria(convocatoriaDisplayLabel(c));
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setShowConvocatoriaDropdown(false);
                        if (form.id_convocatoria) {
                          const c = convocatorias.find(
                            (x) => String(x.nro_convocatoria) === String(form.id_convocatoria)
                          );
                          if (c) setSearchConvocatoria(convocatoriaDisplayLabel(c));
                        }
                      }, 200);
                    }}
                    disabled={loading || loadingPreview}
                  />
                  {showConvocatoriaDropdown && (
                    <div
                      className="border rounded shadow-sm bg-white"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        maxHeight: '320px',
                        overflowY: 'auto',
                        marginTop: 2,
                      }}
                    >
                      {filteredConvocatorias.length > 0 ? (
                        filteredConvocatorias.map((conv) => (
                          <div
                            key={conv.nro_convocatoria}
                            className="p-2 border-bottom"
                            style={{
                              cursor: 'pointer',
                              backgroundColor:
                                String(form.id_convocatoria) === String(conv.nro_convocatoria)
                                  ? '#e7f3ff'
                                  : 'white',
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handlePickConvocatoria(conv);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f0f0f0';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                String(form.id_convocatoria) === String(conv.nro_convocatoria)
                                  ? '#e7f3ff'
                                  : 'white';
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <div>
                                <strong>{conv.COD_CONVOCATORIA}</strong>
                                <span className="text-muted small ms-1">Nº {conv.nro_convocatoria}</span>
                                <div className="small text-muted">{conv.concepto || 'Sin concepto'}</div>
                                <div className="small">
                                  <span className="text-muted">Rubro:</span> {conv.rubro || 'N/A'}
                                </div>
                              </div>
                              <span className={`badge bg-${getEstadoBadgeVariant(conv.estado)} flex-shrink-0`}>
                                {conv.estado}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-muted small">
                          No hay convocatorias que coincidan. Pruebe otro término.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Form.Text className="text-muted">
                  Escriba para filtrar o elija una fila de la lista. Al seleccionar, se cargan el proveedor ganador y
                  el vínculo con la tabla de ganadores.
                </Form.Text>
              </Col>

              <Col md={12}>
                {loadingPreview && (
                  <div className="d-flex align-items-center gap-2 py-2 text-muted">
                    <Spinner animation="border" size="sm" />
                    <span>Cargando datos del proceso…</span>
                  </div>
                )}

                {form.id_convocatoria && !loadingPreview && preview && (
                  <Card className="bg-light border-0">
                    <Card.Body className="py-3">
                      <div className="fw-semibold mb-2 text-secondary small text-uppercase">
                        2. Datos del ganador (solo lectura)
                      </div>
                      {preview.orden_ya_notificada ? (
                        <Alert variant="secondary" className="mb-0 py-2">
                          <i className="bi bi-lock me-2"></i>
                          {preview.mensaje ||
                            'Ya se notificó una orden al proveedor para esta convocatoría. No puede enviar otra notificación.'}
                        </Alert>
                      ) : (
                        <>
                          {!preview.tiene_ganador && (
                            <Alert variant="warning" className="mb-0 py-2">
                              {preview.mensaje ||
                                'No hay ganador registrado en evaluaciones para esta convocatoría. No puede notificar hasta que exista una cotización marcada como ganadora.'}
                            </Alert>
                          )}
                          {preview.tiene_ganador && (
                            <Row className="g-2 small">
                              <Col sm={12}>
                                <span className="text-muted">Proveedor ganador</span>
                                <div className="fw-medium">
                                  {preview.proveedor_razon_social || '—'}
                                  {preview.proveedor_email ? (
                                    <span className="text-muted ms-1">({preview.proveedor_email})</span>
                                  ) : null}
                                </div>
                              </Col>
                              <Col sm={6} md={4}>
                                <span className="text-muted">ID proveedor</span>
                                <div className="font-monospace">{preview.id_proveedor ?? '—'}</div>
                              </Col>
                              <Col sm={6} md={4}>
                                <span className="text-muted">Cotización ganadora</span>
                                <div className="font-monospace">
                                  {preview.id_cotizacion_ganadora != null ? `#${preview.id_cotizacion_ganadora}` : '—'}
                                </div>
                              </Col>
                              {preview.mensaje && (
                                <Col md={12}>
                                  <Alert variant="info" className="mb-0 py-2 mt-1">
                                    {preview.mensaje}
                                  </Alert>
                                </Col>
                              )}
                            </Row>
                          )}
                        </>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </Col>

              {preview?.tiene_ganador && !preview?.orden_ya_notificada && (
                <Col md={4}>
                  <Form.Label className="fw-semibold">
                    ID registro ganador <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    required
                    min={1}
                    placeholder="Ej. 12"
                    value={form.id_ganador}
                    onChange={(e) => onChange('id_ganador', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Identificador en la tabla <code>ganadores</code>. Se rellena solo si existe en base de datos; si
                    no, consúltelo e ingréselo aquí.
                  </Form.Text>
                </Col>
              )}

              <Col md={8}>
                <Form.Label className="fw-semibold">
                  3. PDF de la orden <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf"
                  required
                  disabled={!preview?.tiene_ganador || preview?.orden_ya_notificada}
                  onChange={(e) => onChange('orden_pdf', e.target.files?.[0] || null)}
                />
              </Col>

              <Col md={12}>
                <Form.Label>Observaciones (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.observaciones}
                  disabled={!!preview?.orden_ya_notificada}
                  onChange={(e) => onChange('observaciones', e.target.value)}
                />
              </Col>

              <Col md={12}>
                <Button type="submit" disabled={saving || !puedeEnviar}>
                  {saving ? 'Enviando…' : 'Notificar orden al proveedor'}
                </Button>
                {!puedeEnviar && form.id_convocatoria && !loadingPreview && (
                  <span className="text-muted ms-3 small">
                    {preview?.orden_ya_notificada
                      ? preview.mensaje ||
                        'Ya se notificó la orden para esta convocatoría. No puede enviar otra notificación.'
                      : 'Complete convocatoria con ganador, ID registro ganador si aplica, y el PDF.'}
                  </span>
                )}
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">Historial</h5>
          {loading ? (
            <div className="py-4 text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Convocatoria</th>
                    <th>Proveedor</th>
                    <th>Notificado por</th>
                    <th>Estado</th>
                    <th>Fecha envío</th>
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id_notificacion}>
                      <td>{it.id_notificacion}</td>
                      <td>{it.cod_convocatoria || convLabel(it.id_convocatoria)}</td>
                      <td>{it.proveedor_nombre || it.id_proveedor}</td>
                      <td>{it.notificador_nombre || it.id_usuario_notifica || '—'}</td>
                      <td>{it.estado}</td>
                      <td>{new Date(it.fecha_envio).toLocaleString('es-PE')}</td>
                      <td>
                        <a href={fileUrl(it)} target="_blank" rel="noreferrer">
                          Ver PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrdenesNotificadasAdmin;
